import { getMonthName } from "@repo/shared";
import type { HistoricalStatistics } from "@repo/shared/types";
import { type ReactNode, useState } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useUserLanguage } from "@/lib/i18n/context";
import { useT } from "@/lib/i18n/use-t";
import { formatMonthYear } from "@/lib/utils";
import { T } from "./historical-statistics-panel.i18n";
import { partialPeriods } from "./partial-periods";
import { PeriodReturnsGrid } from "./period-returns-grid";
import { ReturnValue } from "./return-value";

type HistoricalStatisticsPanelProps = {
  statistics: HistoricalStatistics;
  /** The currency the returns are in; null when the series has none stored. */
  currency: string | null;
  /**
   * "calculated": converted and patched for its use (product analysis).
   * "stored": the stored series as it is, no patch or conversion (timeseries admin).
   */
  source: "calculated" | "stored";
  /** Caller-specific facts under the footnote (currencies, points, last price). */
  footer?: ReactNode;
  /**
   * QL-736 — the last price's date when the series ends there instead of being
   * carried to today (no new prices for over 45 days); null or absent otherwise.
   */
  measuredTo?: string | null;
};

/** "YYYY-MM" → a Date in that month, built in UTC so every timezone reads the same month. */
function monthDate(year: number, month: number): Date {
  return new Date(Date.UTC(year, month - 1, 1));
}

/**
 * The Statistics tab (QL-706): calendar statistics of one series from its
 * monthly values — as-of line, key figures, best and worst periods, the month
 * or quarter grid, and a footnote naming what the partial periods are.
 */
export function HistoricalStatisticsPanel({
  statistics: s,
  currency,
  source,
  footer,
  measuredTo,
}: HistoricalStatisticsPanelProps) {
  const t = useT(T);
  const { userLanguage } = useUserLanguage();
  const [mode, setMode] = useState<"months" | "quarters">("months");

  const longDate = (iso: string) => {
    const [year, month, day] = iso.split("-").map(Number);
    return t.asOfDate({ day: day!, month: getMonthName(month! - 1, userLanguage), year: year! });
  };
  const asOf = s.asOf ? longDate(s.asOf) : null;
  const asOfLine = [
    source === "stored" ? t.returnsInStored(currency) : t.returnsIn(currency),
    asOf ? t.trailingTo(asOf) : null,
    measuredTo ? t.toLastPrice(longDate(measuredTo)) : t.toLatestPrice,
  ]
    .filter(Boolean)
    .join(" · ");

  const since = s.sinceStart;
  const keyFigures: Array<{ label: string; value: number | null }> = [
    { label: t.ytd, value: s.ytdPct },
    { label: t.mtd, value: s.mtdPct },
    { label: t.oneYear, value: s.trailing["1Y"]?.returnPct ?? null },
    { label: t.yearsPa(3), value: s.trailing["3Y"]?.annualizedPct ?? null },
    { label: t.yearsPa(5), value: s.trailing["5Y"]?.annualizedPct ?? null },
    { label: t.yearsPa(10), value: s.trailing["10Y"]?.annualizedPct ?? null },
    // Annualised only from twelve months; below that the cumulative return, labelled so.
    since?.annualizedPct != null
      ? { label: t.sinceStartPa, value: since.annualizedPct }
      : { label: since ? t.sinceStart : t.sinceStartPa, value: since?.returnPct ?? null },
  ];

  const monthLabel = (m: { year: number; month: number }, style: "short" | "long" = "short") =>
    formatMonthYear(monthDate(m.year, m.month), userLanguage, { monthStyle: style });

  const partial = partialPeriods(s.months, mode, { ended: !!measuredTo });
  const unit = partial.toDate?.unit ?? "month";
  const toDate = !partial.toDate
    ? null
    : partial.toDate.unit === "quarter"
      ? t.quarterYear(partial.toDate)
      : partial.toDate.unit === "year"
        ? String(partial.toDate.year)
        : monthLabel(partial.toDate, "long");
  const start = partial.start ? monthLabel(partial.start, "long") : null;
  // Ended at an old last price, the latest period runs to that price, not to date.
  const ending = measuredTo ? "lastPrice" : "toDate";
  const footnote =
    toDate && start
      ? t.partialBoth({ toDate, unit, start, ending })
      : toDate
        ? t.partialToDate({ toDate, unit, ending })
        : start
          ? t.partialStart(start)
          : null;
  const returnsInPct = mode === "quarters" ? t.returnsInPctQuarters : t.returnsInPct;

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">{asOfLine}</p>

      <div className="grid grid-cols-2 gap-4 border-y py-4 sm:grid-cols-4 lg:grid-cols-7">
        {keyFigures.map((f) => (
          <div key={f.label} className="space-y-1">
            <div className="text-sm text-muted-foreground">{f.label}</div>
            <div className="text-2xl font-semibold">
              {f.value === null ? (
                <span className="text-muted-foreground">—</span>
              ) : (
                <ReturnValue value={f.value} decimals={2} suffix="%" />
              )}
            </div>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap gap-x-8 gap-y-2 text-sm">
        <BestWorst label={t.bestMonth} period={s.best.month} name={monthLabel} />
        <BestWorst label={t.worstMonth} period={s.worst.month} name={monthLabel} />
        <BestWorst
          label={t.bestYear}
          period={s.best.year}
          name={(y) => String(y.year)}
          empty={t.noFullYear}
        />
        <BestWorst label={t.worstYear} period={s.worst.year} name={(y) => String(y.year)} />
      </div>

      {s.months.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t.noMonths}</p>
      ) : (
        <Tabs value={mode} onValueChange={(v) => setMode(v as "months" | "quarters")}>
          <TabsList>
            <TabsTrigger value="months">{t.months}</TabsTrigger>
            <TabsTrigger value="quarters">{t.quarters}</TabsTrigger>
          </TabsList>
          <TabsContent value="months" className="mt-4">
            <PeriodReturnsGrid statistics={s} mode="months" />
          </TabsContent>
          <TabsContent value="quarters" className="mt-4">
            <PeriodReturnsGrid statistics={s} mode="quarters" />
          </TabsContent>
        </Tabs>
      )}

      <div className="space-y-1 text-xs text-muted-foreground">
        <p>{footnote ? `${returnsInPct} ${footnote}` : returnsInPct}</p>
        {footer && <div className="flex flex-wrap gap-x-6 gap-y-1">{footer}</div>}
      </div>
    </div>
  );
}

/** One "Best month 10.0% Apr 2020" item; "—" (and `empty`, if given) without a complete period. */
function BestWorst<P extends { returnPct: number }>({
  label,
  period,
  name,
  empty,
}: {
  label: string;
  period: P | null;
  name: (p: P) => string;
  empty?: string;
}) {
  return (
    <div className="flex items-baseline gap-2">
      <span className="text-muted-foreground">{label}</span>
      {period ? (
        <>
          <ReturnValue value={period.returnPct} suffix="%" className="font-semibold" />
          <span>{name(period)}</span>
        </>
      ) : (
        <span className="text-muted-foreground">{empty ? `— ${empty}` : "—"}</span>
      )}
    </div>
  );
}
